import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mic, Square, Loader2, Volume2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIDDEN_PREFIXES = ['/login', '/acceso', '/admin', '/portal', '/clinica', '/panel', '/partners/panel', '/agencias/panel', '/autorizar', '/contrato'];
const MAX_FREE = 3;

export const AynaraVoiceOrb = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('idle');
  const [transcript, setTranscript] = useState('');
  const [reply, setReply] = useState('');
  const [handsFree, setHandsFree] = useState(false);
  const [count, setCount] = useState(0);
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const sessionRef = useRef(`voice-${Date.now()}`);

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  const supported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setState('idle'); };

  const speak = async (text) => {
    setState('speaking');
    try {
      const res = await fetch(`${API}/chat/tts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: text.slice(0, 1200) }) });
      if (!res.ok) throw new Error('tts');
      const audio = new Audio(URL.createObjectURL(await res.blob()));
      audioRef.current = audio;
      audio.onended = () => { setState('idle'); if (handsFree) startListening(); };
      await audio.play();
    } catch {
      try { const u = new SpeechSynthesisUtterance(text); u.lang = 'es-ES'; u.onend = () => setState('idle'); speechSynthesis.speak(u); } catch { setState('idle'); }
    }
  };

  const handleCommand = async (text) => {
    const t = text.toLowerCase();
    if (/\b(para|silencio|detente|stop)\b/.test(t)) { stopAudio(); return; }
    if (/whatsapp/.test(t)) { window.open('https://wa.me/17872981758', '_blank'); speak('Te abro nuestro WhatsApp ahora mismo.'); return; }
    if (/(consultor|cita|agendar|reuni)/.test(t)) { speak('Te recomiendo la Consultoría Estratégica de sesenta y nueve dólares: sesión uno a uno con ruta accionable en cuarenta y ocho horas. Te llevo a la agenda.'); setTimeout(() => { window.location.href = '/agenda'; }, 6000); return; }
    if (/(precio|cuánto cuesta|cuanto cuesta|tarifa|planes)/.test(t)) { speak('Tenemos tres formas de empezar: paquetes de activación de pago único, planes mensuales de créditos desde veintinueve dólares, y equipos gestionados. Precio final todo incluido: pagas por lo que consumes. Te llevo a la página de precios.'); setTimeout(() => { window.location.href = '/precios'; }, 9000); return; }
    if (count >= MAX_FREE) { speak('Has usado la demo gratuita de voz. Déjanos tu email en la página de contacto o crea tu cuenta para seguir hablando conmigo sin límite.'); return; }
    setState('processing');
    setCount((c) => c + 1);
    try {
      const res = await fetch(`${API}/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: text, session_id: sessionRef.current }) });
      const data = await res.json();
      const answer = (data.response || 'No he podido procesar tu mensaje.').slice(0, 800);
      setReply(answer);
      await speak(answer);
    } catch { setState('idle'); }
  };

  const startListening = () => {
    if (!supported) return;
    stopAudio();
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'es-ES'; rec.interimResults = true; rec.continuous = false;
    rec.onresult = (e) => {
      const txt = Array.from(e.results).map((r) => r[0].transcript).join(' ');
      setTranscript(txt);
      if (e.results[e.results.length - 1].isFinal) { rec.stop(); handleCommand(txt); }
    };
    rec.onerror = () => setState('idle');
    rec.onend = () => { if (state === 'listening') setState('idle'); };
    recRef.current = rec;
    setTranscript(''); setState('listening');
    rec.start();
  };

  const toggle = () => {
    if (!open) { setOpen(true); startListening(); return; }
    if (state === 'listening' && recRef.current) { recRef.current.stop(); setState('idle'); return; }
    if (state === 'speaking') { stopAudio(); return; }
    startListening();
  };

  useEffect(() => () => { if (recRef.current) recRef.current.abort?.(); stopAudio(); }, []);

  if (hidden) return null;

  const orbColor = state === 'listening' ? 'bg-sky-500 animate-pulse' : state === 'processing' ? 'bg-amber-500' : state === 'speaking' ? 'bg-emerald-500' : 'bg-[#0a1a3f] border border-[#00d4ff]/50';

  return (
    <>
      {open && (
        <div className="fixed bottom-44 right-5 z-[60] w-[300px] rounded-2xl border border-[#00d4ff]/30 bg-[#040a14]/95 p-4 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.5)]" data-testid="aynara-voice-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-widest text-[#7de9ff]">🎙️ Aynara Voz {state === 'listening' && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-emerald-400" />}</p>
            <button onClick={() => { stopAudio(); setOpen(false); }} className="text-slate-500 hover:text-white text-sm" data-testid="aynara-voice-close">✕</button>
          </div>
          <p className="mt-2 min-h-[20px] text-xs text-slate-300">{state === 'listening' ? (transcript || 'Escuchando… habla ahora') : state === 'processing' ? 'Pensando…' : state === 'speaking' ? 'Hablando…' : supported ? 'Toca el orbe para hablar. Pulsa «Permitir» cuando tu navegador lo pida.' : 'Tu navegador no soporta voz — usa el chat de texto 💬'}</p>
          {reply && state !== 'listening' && <p className="mt-2 max-h-24 overflow-y-auto rounded-lg bg-white/5 p-2 text-[11px] text-slate-200">{reply}</p>}
          <label className="mt-3 flex items-center gap-2 text-[11px] text-slate-400">
            <input type="checkbox" checked={handsFree} onChange={(e) => setHandsFree(e.target.checked)} data-testid="aynara-voice-handsfree" /> Modo manos libres
          </label>
        </div>
      )}
      <button onClick={toggle} className={`fixed bottom-40 right-6 z-[60] flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg transition hover:scale-105 ${orbColor}`} title="Hablar con Aynara" data-testid="aynara-voice-orb">
        {state === 'processing' ? <Loader2 className="h-5 w-5 animate-spin" /> : state === 'speaking' ? <Volume2 className="h-5 w-5" /> : state === 'listening' ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5 text-[#00d4ff]" />}
      </button>
    </>
  );
};

export default AynaraVoiceOrb;
